import { gql } from "@apollo/client";
import { BuildingIcon, CoinsIcon } from "lucide-react";

import { TaxFragment } from "@/__generated__/graphql";
import { TableColumnType } from "@/components/@types";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { FieldTable } from "@/components/organisms/Field";
import {
  useNodeField,
  useNodeLayoutStore,
} from "@/components/organisms/Node/Node.hooks";
import { Values } from "@/constants/values";
import { icons, iconsByTypename } from "@/objects/@icons";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

function TaxRatesTab() {
  const store = useNodeLayoutStore();
  const { fieldDefinition, fieldEvents, fieldValues } = useNodeField(
    store,
    "rates",
  );

  if (!fieldDefinition || fieldDefinition.type !== "table") {
    return null;
  }

  const value = (fieldDefinition.getValue(fieldValues) ??
    []) as TaxFragment["rates"];
  const valueOptions = (fieldDefinition.getValueOptions?.(fieldValues) ?? {
    columns: [],
  }) as { columns: TableColumnType[] };

  return (
    <div className="flex-1 min-h-0 w-full max-w-full flex flex-col overflow-hidden">
      <FieldTable
        value={value}
        valueOptions={valueOptions}
        events={fieldEvents}
        style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

/** A tax as a card: what it charges in all, and the rates it is made of. */
function TaxCard({ data, isLoading = false }: ObjectCardProps<TaxFragment>) {
  const rates = (data?.rates ?? []).filter(Boolean);
  return (
    <div className="flex flex-col gap-1.5 h-full min-h-0">
      {data?.totalRate != null && (
        <Typography
          level="h4"
          className="[line-height:1.1] [font-variant-numeric:tabular-nums]"
        >
          <Skeleton loading={isLoading} variant="inline">
            {`${Number(data.totalRate)}%`}
          </Skeleton>
        </Typography>
      )}
      {rates.length > 0 && (
        <Typography level="body-sm" className="truncate text-muted-foreground">
          <Skeleton loading={isLoading} variant="inline">
            {rates
              .map((rate) => `${rate!.name} ${Number(rate!.rate)}%`)
              .join(" · ")}
          </Skeleton>
        </Typography>
      )}
      {data?.description && (
        <Typography
          level="body-sm"
          className="text-muted-foreground/80 line-clamp-2"
        >
          <Skeleton loading={isLoading} variant="inline">
            {data.description}
          </Skeleton>
        </Typography>
      )}
    </div>
  );
}

const object: ObjectType<TaxFragment> = {
  humanName: "tax",
  inputKey: "tax",
  typename: "TaxObject",
  report: {
    source: "taxes",
    description: "The rates charged and paid",
    asks: "How much tax has been collected this quarter?",
  },
  ReactIcon: CoinsIcon,
  titleFieldKey: "name",
  getTitle: (data) => {
    return data?.name || "";
  },
  Card: TaxCard,
  placeholder: {
    __typename: "TaxObject",
    id: btoa("TaxObject:placeholder"),
    identifier: "placeholder",
    name: "HST Ontario",
    description: "Harmonized sales tax on goods and services",
    totalRate: "13",
    rates: [
      {
        id: btoa("TaxRateObject:placeholder"),
        identifier: "placeholder",
        name: "HST",
        rate: "13",
      },
    ],
  } as TaxFragment,
  getEntityId: (data) => {
    return data?.ledgerEntity?.id ?? null;
  },
  getReferenceValueOptions: (entityIdentifiers) => {
    return {
      query: gql`
        query TaxList($entityIdentifiers: [String!]) {
          taxes(entityIdentifiers: $entityIdentifiers) {
            data {
              ...Tax
            }
          }
        }
      `,
      queryOptions: {
        variables: {
          entityIdentifiers: entityIdentifiers,
        },
      },
      onRenderOption: (option: TaxFragment) => {
        // Matches how a chosen tax reads once it is on a line: the rate is what
        // distinguishes two taxes with similar names.
        const totalRate = (option.rates ?? []).reduce(
          (sum, rate) => sum + Number(rate?.rate ?? 0),
          0,
        );
        return totalRate ? `${option.name} (${totalRate}%)` : option.name;
      },
      mutationOptions: {
        createNewLabel: "Create new tax",
        createTypename: "TaxObject",
      },
    };
  },
  groups: [
    {
      id: "breakdown",
      label: "Breakdown",
      isVisible: true,
      component: TaxRatesTab,
      flush: true,
      card: {
        isLabelVisible: false,
        flex: 1,
        isFlush: true,
      },
    },
  ],
  fieldDefinitions: [
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.ledgerEntity?.identifier,
      getValue: (data) => () => {
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoxForEntity
              identifier={data?.ledgerEntity?.identifier}
              representation="character"
            />
          </div>
        );
      },
      fieldProps: {
        exportPath: "ledgerEntity",
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: false,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Tax code",
      type: "reference",
      ReactIcon: iconsByTypename.TaxObject,
      asString: (data) => data?.identifier,
      getValue: (data) => {
        return {
          __typename: "TaxObject",
          name: data?.name,
          id: data?.id,
          identifier: data?.identifier,
          entityId: data?.ledgerEntity?.id,
        };
      },
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("TaxObject")([
          data?.ledgerEntity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: icons.name,
      asString: (data) => data?.name || "",
      getValue: (data) => {
        return data?.name;
      },
      // The title field stays visible so it reaches the node store and the
      // title bar can edit it — ``NodeLayout`` already drops ``titleFieldKey``
      // from the field rows, so it never doubles up as one.
      createProps: {
        isRequired: true,
        isEditable: true,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "description",
      label: "Description",
      type: "text",
      ReactIcon: icons.description,
      asString: (data) => data?.description || "",
      getValue: (data) => {
        return data?.description;
      },
      createProps: {
        isRequired: false,
        isEditable: true,
        isVisible: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
        isVisible: false,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "totalRate",
      linkedValuesKeys: ["totalRate", "rates"],
      label: "Total Rate",
      type: "percent",
      ReactIcon: icons.percentage,
      asString: (data) => data?.totalRate || "",
      getValue: (data) => {
        return (
          data?.rates?.reduce(
            (sum, rate) => sum + (parseFloat(rate.rate) || 0),
            0,
          ) ||
          data?.totalRate ||
          0
        );
      },
      createProps: {
        isRequired: false,
        isEditable: false,
        isVisible: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: false,
        isVisible: false,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "rates",
      label: "Rates",
      type: "table",
      listInputFormat: "array",
      // ``_entityIdentifier`` rides along so the account picker below can be
      // scoped to it — ``useNodeField`` hands a field only the keys it links.
      linkedValuesKeys: ["rates", "_entityIdentifier"],
      ReactIcon: icons.tags,
      groupId: "breakdown",
      asString: () => "",
      getValue: (data) => {
        return data?.rates || [];
      },
      getValueOptions: (data) => {
        // A rate posts to an account in the tax's own entity. For a tax created
        // from another record's field that is the entity the record belongs to
        // (seeded by the inline create card); for a saved tax it is wherever
        // its existing rates already post. Falling through to the app's scoped
        // entity offered accounts the tax could not use, so the save failed on
        // a form the user had filled in completely.
        const entityIdentifier =
          (data as { _entityIdentifier?: string | null })?._entityIdentifier ||
          data?.rates?.find((rate) => rate?.chartedAccount?.entity?.identifier)
            ?.chartedAccount?.entity?.identifier;

        return {
          columns: [
            {
              objectKey: "name",
              label: "Name",
              type: "string",
              getValue: (row: any) => row?.name || "",
              getValueOptions: () => {
                return {
                  placeholder: "Eg., Provincial sales tax",
                };
              },
            },
            {
              objectKey: "description",
              label: "Description",
              type: "text",
              getValue: (row: any) => row?.description || "",
              getValueOptions: () => {
                return {
                  placeholder: "Payable to the government",
                };
              },
            },
            {
              objectKey: "rate",
              label: "Rate",
              type: "percent",
              getValue: (row: any) => row?.rate || "",
              // A new row is seeded with "" for every cell, which the API
              // rejects outright for a Decimal. An unfilled rate is 0%.
              getValueForApi: (row: any) => Number(row?.rate) || 0,
              getValueOptions: () => {
                return {
                  placeholder: "3%",
                };
              },
            },
            {
              label: "Account",
              type: "reference",
              width: 100,
              headerFieldType: "string",
              objectKey: "account",
              inputKey: "chartedAccount",
              isDisabled: false,
              isSearchable: false,
              getValue: (row: any) => ({
                __typename: "ChartedAccountObject",
                id: row?.chartedAccount?.id ?? row?.account?.id,
                identifier:
                  row?.chartedAccount?.identifier ?? row?.account?.identifier,
                name: row?.chartedAccount?.name ?? row?.account?.name,
              }),
              getValueForApi: (row: any) => ({
                identifier:
                  row?.account?.identifier ?? row?.chartedAccount?.identifier,
              }),
              getValueOptions: () =>
                getReferenceValueOptionsByTypename("ChartedAccountObject")(
                  entityIdentifier ? [entityIdentifier] : [],
                ),
            },
          ],
        };
      },
      createProps: {
        isRequired: true,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
    },
  ],
};

export { object as TaxObject };
